import React, { useCallback, useRef } from 'react';
import { BlurView } from 'expo-blur';
import { Link, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

import { Icon, theme, useHovered, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsLink = (props: Props) => {
  const { href, name, slug, onPress } = props;

  const { formatMessage } = useIntl();
  const router = useRouter();

  const containerRef = useRef<View>(null);
  const isHovered = useHovered(containerRef);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigate = useCallback(() => {
    router.navigate(href);
    onPress?.();
  }, [href, router, onPress]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const linkStyles = [styles.link, isHovered ? styles.linkHover : undefined, linkWebStyles];

  // ─────────────────────────────────────────────────────────────────────

  if (Platform.OS === 'web') {
    return (
      <View
        ref={containerRef}
        style={styles.container}
        pointerEvents="box-none"
        {...{ dataSet: { baseProductSlug: slug } }}
      >
        <BlurView intensity={BLUR_INTENSITY} style={styles.blurView} />

        <Link
          href={href}
          style={linkStyles}
          aria-label={formatMessage(messages.label, { name })}
          onPress={onPress}
        >
          <Icon
            name="IconBook"
            width={24}
            height={24}
            color={theme.colors.KALE}
            pointerEvents="none"
          />
        </Link>
      </View>
    );
  }

  return (
    <TouchableOpacity
      ref={containerRef}
      style={styles.container}
      onPress={navigate}
      activeOpacity={0.7}
      aria-label={formatMessage(messages.label, { name })}
      accessibilityRole="button"
    >
      <BlurView intensity={BLUR_INTENSITY} style={styles.blurView} />

      <View style={linkStyles}>
        <Icon
          name="IconBook"
          width={24}
          height={24}
          color={theme.colors.KALE}
          pointerEvents="none"
        />
      </View>
    </TouchableOpacity>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  label: {
    defaultMessage: 'Navigate to "{name}" details screen',
    description: 'Product Details | Link button | Label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const BUTTON_SIZE = 48;
const BLUR_INTENSITY = 45;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: theme.spacing['4'],
    left: theme.spacing['4'],
    zIndex: 1,
    borderRadius: BUTTON_SIZE,
  },
  blurView: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: BUTTON_SIZE,
    overflow: 'hidden',
  },
  link: {
    display: 'flex', // required
    alignItems: 'center',
    justifyContent: 'center',
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE,
    backgroundColor: theme.colors.OPACITY.CREAM.DARKER,
  },
  linkHover: {
    opacity: 0.8,
  },
});

const linkWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: `opacity ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  href: string;
  name: string;
  slug: string;
  onPress?: () => void;
};
